<template>
  <b-modal
    id="self-clock-out-modal"
    modal-class="timesheet-modal"
    title="Clock Out"
    ok-title="Clock Out"
    :ok-disabled="!canSubmit || submitting"
    :busy="submitting"
    @ok.prevent="submit"
    @hidden="reset"
    @shown="onShown"
  >
    <b-form-group label="Break (minutes)">
      <b-form-input
        v-model.number="breakMinutes"
        type="range"
        min="0"
        max="120"
        step="5"
        :disabled="submitting"
      />
      <div class="text-muted small">{{ breakMinutes }} min</div>
    </b-form-group>

    <b-form-group label="Comment">
      <b-form-textarea
        v-model="comment"
        maxlength="500"
        rows="3"
        placeholder="What did you get done? (required if no photo)"
        :disabled="submitting"
      />
    </b-form-group>

    <b-form-group label="Photos">
      <clock-out-photo-upload v-model="photos" />
    </b-form-group>

    <b-form-group label="Plant / Equipment used">
      <div v-if="equipmentStatus === 'loading'" class="text-muted small">
        Loading equipment...
      </div>
      <div v-else-if="equipmentStatus === 'error'" class="text-muted small">
        Couldn't load the equipment list.
        <b-button
          variant="link"
          size="sm"
          class="p-0 align-baseline"
          @click="loadEquipment"
        >
          Retry
        </b-button>
      </div>
      <div v-else-if="!equipmentOptions.length" class="text-muted small">
        No plant and equipment available for this project.
      </div>
      <template v-else>
        <plant-entry-row
          v-for="(entry, i) in plantEntries"
          :key="i"
          :value="entry"
          :equipment-options="equipmentChoices"
          :min-quantity="1"
          :taken-ids="takenIdsFor(i)"
          :disabled="submitting"
          @input="updatePlantEntry(i, $event)"
          @remove="removePlantEntry(i)"
        />
        <b-button
          variant="outline-secondary"
          size="sm"
          :disabled="submitting || allEquipmentAdded"
          @click="addPlantEntry"
        >
          + Add equipment
        </b-button>
      </template>
    </b-form-group>

    <div class="geofence-line text-muted small mb-2">
      <b-icon icon="geo-alt" class="mr-1" />
      {{ geofence.label }}
    </div>

    <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import { geofenceLabel, tryGetPosition } from '@/utils/geofence'
import {
  clampPlantQuantity,
  plantEntryErrorMessage,
} from '@/utils/timesheetHelpers'
import PlantEntryRow from '@/components/timesheets/shared/PlantEntryRow'
import ClockOutPhotoUpload from './ClockOutPhotoUpload'

export default {
  name: 'ClockOutDialog',

  components: { PlantEntryRow, ClockOutPhotoUpload },

  props: {
    // The currently open clocked-in event, so we know which project's
    // zones to check geofence against and what the default break is.
    activeEvent: { type: Object, default: null },
    project: { type: Object, default: null },
  },

  data() {
    return {
      breakMinutes: 0,
      comment: '',
      photos: [],
      plantEntries: [],
      equipmentOptions: [],
      equipmentStatus: 'loading',
      position: null,
      geofence: { label: 'Location unavailable', zoneId: null },
      submitting: false,
      error: null,
    }
  },

  computed: {
    canSubmit() {
      return this.comment.trim().length > 0 || this.photos.length > 0
    },

    equipmentChoices() {
      return [
        { value: null, text: 'Select equipment' },
        ...this.equipmentOptions,
      ]
    },

    // Each item can be added once, so no more rows once every item is on one.
    allEquipmentAdded() {
      return this.plantEntries.length >= this.equipmentOptions.length
    },

    equipmentProjectId() {
      return (
        (this.activeEvent && this.activeEvent.project_id) ||
        (this.project && this.project.id) ||
        null
      )
    },
  },

  methods: {
    ...mapActions('timesheet', ['clockOutSelf']),
    ...mapActions('plantEquipment', ['getMyPlantEquipment']),

    async onShown() {
      this.breakMinutes =
        (this.project && this.project.default_break_minutes) || 0

      this.loadEquipment()

      this.position = await tryGetPosition()
      const zones = (this.project && this.project.zones) || []
      this.geofence = this.position
        ? geofenceLabel(zones, this.position.lat, this.position.lng)
        : { label: 'Location unavailable', zoneId: null }
    },

    // /plant-equipment/mine needs the project; without it the API returns 422.
    async loadEquipment() {
      if (!this.equipmentProjectId) {
        this.equipmentOptions = []
        this.equipmentStatus = 'ready'
        return
      }
      this.equipmentStatus = 'loading'
      try {
        const items = await this.getMyPlantEquipment({
          project_id: this.equipmentProjectId,
        })
        this.equipmentOptions = items.map((i) => ({
          value: i.id,
          text: i.name,
          rateType: i.rate_type,
        }))
        this.equipmentStatus = 'ready'
      } catch (e) {
        this.equipmentOptions = []
        this.equipmentStatus = 'error'
      }
    },

    addPlantEntry() {
      this.plantEntries.push({ plant_equipment_id: null, quantity: 1 })
    },

    // Items picked on the other rows, which this row can no longer choose.
    takenIdsFor(index) {
      return this.plantEntries
        .filter((e, i) => i !== index && e.plant_equipment_id)
        .map((e) => e.plant_equipment_id)
    },

    updatePlantEntry(index, value) {
      this.plantEntries.splice(index, 1, value)
    },

    removePlantEntry(index) {
      this.plantEntries.splice(index, 1)
    },

    async submit() {
      if (!this.canSubmit || this.submitting) return
      this.submitting = true
      this.error = null

      try {
        const formData = new FormData()
        formData.append('lat', this.position ? this.position.lat : 0)
        formData.append('lng', this.position ? this.position.lng : 0)
        formData.append('break_minutes', this.breakMinutes)
        if (this.comment.trim()) formData.append('comment', this.comment.trim())
        this.photos.forEach((file, i) => {
          formData.append(`photos[${i}]`, file, file.name)
        })
        this.plantEntries
          .filter((e) => e.plant_equipment_id)
          .forEach((e, i) => {
            formData.append(
              `plant_entries[${i}][plant_equipment_id]`,
              e.plant_equipment_id,
            )
            formData.append(
              `plant_entries[${i}][quantity]`,
              clampPlantQuantity(e.quantity, 1),
            )
          })

        const event = await this.clockOutSelf(formData)
        this.$emit('clocked-out', event)
        this.$bvModal.hide('self-clock-out-modal')
      } catch (e) {
        const data = e.response && e.response.data
        this.error =
          plantEntryErrorMessage(data) ||
          (data && data.message) ||
          'Something went wrong clocking out - try again.'
      } finally {
        this.submitting = false
      }
    },

    reset() {
      this.breakMinutes = 0
      this.comment = ''
      this.photos = []
      this.plantEntries = []
      this.equipmentStatus = 'loading'
      this.position = null
      this.geofence = { label: 'Location unavailable', zoneId: null }
      this.error = null
    },
  },
}
</script>
